import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Option, SortOption, SortType } from '@anytag/shared-types';
import { LIMIT, LIMIT_50, LIMIT_100 } from '@anytag/shared-constants';
import { AiSidebarButton, CheckBox, Form } from '@anytag/shared-atoms';
import { Filter, SelectorField, SingleSelectorFieldProps } from '@anytag/shared-molecules';
import { InfluencersSortFormValues, SortSelectButtonProps } from '../schemaTypes';

interface TemplateProps<T extends string> {
  sortOptions?: SortOption<T>[];
  setLimit: (limit: number) => void;
  setSort: (sort: SortType<T>) => void;
  selectButtonProps?: SortSelectButtonProps;
}

const Template = <T extends string>({ setLimit, setSort, sortOptions, selectButtonProps }: TemplateProps<T>) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<{ [key: string]: string | string[] }>();

  const limitOptions: Option<string, string>[] = [
    { value: String(LIMIT), label: t<string>('View_limit', { count: LIMIT }) },
    { value: String(LIMIT_50), label: t<string>('View_limit', { count: LIMIT_50 }) },
    { value: String(LIMIT_100), label: t<string>('View_limit', { count: LIMIT_100 }) }
  ];

  const handleSortChange: SingleSelectorFieldProps<keyof InfluencersSortFormValues<T>, SortOption<T>>['onChange'] = (
    field,
    value,
    options
  ) => {
    const selectedOption = options.find((option) => option.value === value);
    setValue(field, value);
    setSort({ field: selectedOption?.field || null, order: selectedOption?.order || null });
  };

  const handleLimitChange = (field: string, value: string) => {
    setValue(field, value);
    setLimit(Number(value));
  };

  return (
    <Form.Fields className="items-center">
      {selectButtonProps ? (
        <Form.Field className="flex flex-none items-center">
          <CheckBox
            className="text-base font-semibold"
            help={t(selectButtonProps.hasAllSelected ? 'Unselect all on this page' : 'Select all on this page')}
            {...selectButtonProps}
          />
          <AiSidebarButton buttonText="Button.Find Influencers" className="ml-4 min-w-max items-center" />
        </Form.Field>
      ) : null}

      {sortOptions ? (
        <SelectorField<keyof InfluencersSortFormValues<T>, SortOption<T>>
          name="sort"
          options={sortOptions}
          onChange={handleSortChange}
          placeholder={t<string>('Selector.Sort')}
          className="ml-auto max-w-max min-w-30"
        />
      ) : null}

      <SelectorField<keyof InfluencersSortFormValues<T>>
        name="limit"
        clearable={false}
        options={limitOptions}
        className="max-w-30"
        onChange={handleLimitChange}
      />
    </Form.Fields>
  );
};

interface InfluencersSortTemplateProps<T extends string> extends TemplateProps<T> {
  initialValues: InfluencersSortFormValues<T>;
}

export const InfluencersSortTemplate = <T extends string>({
  sortOptions,
  initialValues,
  ...restProps
}: InfluencersSortTemplateProps<T>) => (
  <Filter.Provider
    className="rounded-none py-0"
    values={{
      limit: initialValues.limit,
      sort:
        sortOptions?.find((o) => o.field === initialValues.sort.field && o.order === initialValues.sort.order)?.value ||
        undefined
    }}
  >
    <Template<T> sortOptions={sortOptions} {...restProps} />
  </Filter.Provider>
);
